import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { proposals } from "@/lib/api";

const STATUS_PILL = {
  draft: "pill-gray", in_progress: "pill-blue", in_review: "pill-purple",
  submitted: "pill-green", abandoned: "pill-red",
};

export default function ActiveResponses() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setItems((await proposals.list()).filter((p) => p.status !== "archived"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const statuses = useMemo(() => Array.from(new Set(items.map((p) => p.status).filter(Boolean))).sort(), [items]);
  const filtered = useMemo(() => items.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (search && !`${p.title} ${p.agency}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [items, search, statusFilter]);

  const archive = async (p) => { setBusy(p.id); try { await proposals.archive(p.id); await load(); } catch (e) { window.alert(e?.response?.data?.detail || "Archive failed"); } finally { setBusy(""); } };
  const remove = async (p) => {
    if (!window.confirm(`Permanently delete "${p.title}"? This cannot be undone.`)) return;
    setBusy(p.id); try { await proposals.remove(p.id); await load(); } catch (e) { window.alert(e?.response?.data?.detail || "Delete failed"); } finally { setBusy(""); }
  };

  return (
    <div className="page" data-testid="page-active-responses">
      <div className="page-header">
        <div>
          <div className="page-title">Active Responses</div>
          <div className="page-subtitle">In-progress proposals · {filtered.length} shown</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate("/new-response")} data-testid="active-new-btn">+ New Response</button>
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <span className="search-icon">🔍</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title or agency…" data-testid="active-search" />
        </div>
        <select className="select-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="active-status-filter">
          <option value="all">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Title</th><th>Agency</th><th>Type</th><th>Status</th><th>Start Date</th><th>Due</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><div className="center-load"><span className="spinner" /> Loading…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 28, color: "var(--text-secondary)" }}>
                No active responses yet — use “+ New Response” above to start one.
              </td></tr>
            ) : filtered.map((p) => (
              <tr key={p.id} data-testid={`active-row-${p.id}`}>
                <td style={{ cursor: "pointer", fontWeight: 600 }} onClick={() => navigate(`/workspace?proposal=${p.id}`)}>{p.title}</td>
                <td>{p.agency}</td>
                <td>{p.rfp_type}</td>
                <td><span className={`pill ${STATUS_PILL[p.status] || "pill-blue"}`}>{(p.status || "").replace(/_/g, " ")}</span></td>
                <td>{p.created_at ? p.created_at.slice(0, 10) : "—"}</td>
                <td>{p.due_date || "—"}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/workspace?proposal=${p.id}`)}>Open</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => archive(p)} disabled={busy === p.id} data-testid={`archive-${p.id}`}>Archive</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: "var(--danger)" }} onClick={() => remove(p)} disabled={busy === p.id} data-testid={`delete-${p.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
